/**
 * Bill Validation — how far a bill has got from invoice to report.
 *
 *   assigned → bill period → parsed → NMI match
 *     → bill run: usage data → network charges → retail TOU charges
 *     → bill run calculated → report created
 *
 * The Bulk Bill Run board shows every active bill with these stages and, as its
 * status, the first one that has not passed and why. Derived on every read from
 * data that already exists — nothing here is stored, so it cannot go stale.
 *
 * Dependency-free apart from effectiveInvoice, so it is tested directly.
 */

import type { BvBill, ShadowBillResult } from '../types/billTypes';
import type { ParseState } from '../utils/parseState';
import { effectiveInvoiceResult } from './effectiveInvoice';
import { hasReport, networkEnergyNotPriced, networkNotPriced } from './reportStatus';
import { isPlausibleNmi } from '../utils/nmiIdentity';

export type StageKey =
  | 'assigned' | 'period' | 'parsed' | 'nmi'
  | 'usage' | 'network' | 'tou'
  | 'calculated' | 'report';

export type StageState = 'pass' | 'fail' | 'waiting' | 'running';

export type Stage = { key: StageKey; state: StageState; detail: string | null };

/**
 * Where a bill is stuck, for the board's filter chips. Usage data, network
 * charges, retail TOU and the calculation are all one phase: the bill run.
 */
export type PipelinePhase = 'period' | 'parsed' | 'nmi' | 'calculated' | 'report' | 'done';

export type PipelineResult = {
  stages: Stage[];
  /** First stage not passed, or 'done'. */
  current: StageKey | 'done';
  phase: PipelinePhase;
  /** The status the board shows: the reason at `current`, or "Report ready". */
  label: string;
};

export const STAGE_LABELS: Record<StageKey, string> = {
  assigned: 'Assigned',
  period: 'Bill period',
  nmi: 'NMI match',
  parsed: 'Parsed',
  usage: 'Usage data',
  network: 'Network charges',
  tou: 'Retail TOU',
  calculated: 'Bill run',
  report: 'Report',
};

export const STAGE_ORDER: StageKey[] = [
  'assigned', 'period', 'parsed', 'nmi', 'usage', 'network', 'tou', 'calculated', 'report',
];

/** bv_bill_run_queue (084/111), as far as the board needs it. */
export type QueueLike = {
  coverage_pct: number | string | null;
  days_covered: number | null;
  days_needed: number | null;
  blocked_reason: string | null;
};

/** bv_nem_analysis (056) step statuses for the bill's period. */
export type AnalysisLike = {
  data_status: 'idle' | 'running' | 'done' | 'error';
  data_error: string | null;
  tariff_status: 'idle' | 'running' | 'done' | 'error';
  tariff_error: string | null;
  tou_status: 'idle' | 'running' | 'done' | 'error';
  tou_error: string | null;
};

export type PipelineInput = {
  bill: Partial<BvBill>;
  parseState: ParseState;
  queue: QueueLike | null;
  analysis: AnalysisLike | null;
  /** Other active bills on the same site and exact period (see twinCounter). */
  twins?: Twins | null;
};

/** Other active bills on the same site for the exact same period. */
export type Twins = { count: number; withInvoice: number };

/**
 * Count each bill's twins: other active bills on the same site and exact
 * period, and how many of them hold an invoice. Pass active bills only.
 */
export function twinCounter(bills: Partial<BvBill>[]) {
  const groups = new Map<string, Partial<BvBill>[]>();
  const keyOf = (b: Partial<BvBill>) =>
    b.site_id && b.bill_from && b.bill_to ? `${b.site_id}|${b.bill_from}|${b.bill_to}` : null;
  for (const b of bills) {
    const k = keyOf(b);
    if (!k) continue;
    const g = groups.get(k);
    if (g) g.push(b);
    else groups.set(k, [b]);
  }
  return (bill: Partial<BvBill>): Twins => {
    const k = keyOf(bill);
    const others = (k ? groups.get(k) ?? [] : []).filter((b) => b !== bill && b.id !== bill.id);
    return { count: others.length, withInvoice: others.filter((b) => !!effectiveInvoiceResult(b)).length };
  };
}

/** An NMI is 10 characters; invoices often print an 11th checksum digit. */
export function normaliseNmi(v: string | null | undefined): string | null {
  const s = String(v ?? '').replace(/\s+/g, '').toUpperCase();
  return s ? s.slice(0, 10) : null;
}

const pass = (key: StageKey, detail: string | null = null): Stage => ({ key, state: 'pass', detail });
const fail = (key: StageKey, detail: string): Stage => ({ key, state: 'fail', detail });
const waiting = (key: StageKey, detail: string): Stage => ({ key, state: 'waiting', detail });
const running = (key: StageKey, detail: string): Stage => ({ key, state: 'running', detail });

const PARSE_DETAIL: Record<ParseState, string> = {
  parsed: '',
  parsing: 'Parsing the invoice',
  pending: 'Invoice not parsed yet',
  stalled: 'Invoice parse stalled',
  failed: 'Invoice parse failed',
  none: 'No invoice attached',
};

function analysisStage(
  key: 'usage' | 'network' | 'tou',
  status: AnalysisLike['data_status'] | undefined,
  error: string | null | undefined,
): Stage | null {
  if (status === 'done') return pass(key);
  if (status === 'running') return running(key, `${STAGE_LABELS[key]}: running`);
  if (status === 'error') return fail(key, `${STAGE_LABELS[key]}: ${error ?? 'failed'}`);
  return null; // idle / no row — decided from the queue below
}

export function billPipeline({ bill, parseState, queue, analysis, twins }: PipelineInput): PipelineResult {
  const stages: Stage[] = [];
  const invoice = effectiveInvoiceResult(bill);
  const priced = !!bill.shadow_result;

  // 1. Assigned. Every bill row is on a site; an unassigned email is not a bill.
  stages.push(bill.site_id ? pass('assigned') : fail('assigned', 'Not assigned to a site'));

  // 2. Bill period. Two active bills that both hold an invoice for the exact
  // same period double-count in every total; one is superseded.
  if (!bill.bill_from || !bill.bill_to) stages.push(fail('period', 'No bill period'));
  else if (invoice && twins?.withInvoice) {
    stages.push(fail('period', 'Another active bill covers this same period — archive the superseded one'));
  } else stages.push(pass('period'));

  // 3. Parsed. The NMI is read from the parsed invoice, so this comes first.
  if (parseState === 'parsed' || invoice) stages.push(pass('parsed'));
  else if (parseState === 'parsing') stages.push(running('parsed', PARSE_DETAIL.parsing));
  else if (parseState === 'pending') stages.push(waiting('parsed', PARSE_DETAIL.pending));
  else if (parseState === 'none' && twins?.withInvoice) {
    // An empty copy of a period whose invoice sits on another bill.
    stages.push(fail('parsed', 'Duplicate — the invoice is on another bill for this period; archive this one'));
  }
  else stages.push(fail('parsed', PARSE_DETAIL[parseState]));

  // 4. NMI match — the NMI printed on the invoice against the site's.
  // A consolidated invoice bills several NMIs (extracted_nmis); it matches when
  // the site's is among them, whichever one the parser listed first.
  const siteNmi = normaliseNmi(bill.site?.nmi);
  const rawInvoiceNmi = invoice?.extracted_nmi ? String(invoice.extracted_nmi).replace(/\s+/g, '') : null;
  const invoiceNmi = normaliseNmi(rawInvoiceNmi);
  const invoiceNmis = [invoiceNmi, ...(invoice?.extracted_nmis ?? []).map(normaliseNmi)]
    .filter((n): n is string => !!n);
  if (!invoice) {
    stages.push(waiting('nmi', 'Checked once the invoice is parsed'));
  } else if (!invoiceNmi) {
    stages.push(pass('nmi', 'No NMI printed on the invoice — not checked'));
  } else if (!siteNmi || invoiceNmis.includes(siteNmi)) {
    stages.push(pass('nmi'));
  } else if (!isPlausibleNmi(rawInvoiceNmi)) {
    // Not a real NMI (wrong length or checksum): the parser misread it, so
    // this says nothing about which meter the invoice is for.
    stages.push(fail('nmi', `NMI misread on the invoice (${rawInvoiceNmi} is not a valid NMI) — correct it in the invoice editor`));
  } else {
    stages.push(fail('nmi', `NMI on invoice ${invoiceNmi} ≠ site ${siteNmi}`));
  }

  // 5–7. Bill run inputs. A priced bill without an analysis row was calculated by
  // hand from the bill page; these stages are done by definition.
  const blocked = queue?.blocked_reason ?? null;
  const noTariff = !!blocked && /distributor|network tariff/i.test(blocked);

  const usage = analysisStage('usage', analysis?.data_status, analysis?.data_error);
  if (usage) stages.push(usage);
  else if (priced) stages.push(pass('usage'));
  else if (queue && Number(queue.coverage_pct) >= 100) stages.push(pass('usage'));
  else if (queue && queue.days_covered) {
    // Worded from the counts, not the queue's "interval data covers …" text:
    // the board speaks of usage data throughout.
    stages.push(waiting('usage', `Usage data not yet available — covers ${queue.days_covered} of ${queue.days_needed} days`));
  } else if (queue && blocked && !noTariff && !/interval data/i.test(blocked)) {
    stages.push(waiting('usage', capitalise(blocked)));
  } else stages.push(waiting('usage', 'Usage data not yet available for the bill period'));

  const network = analysisStage('network', analysis?.tariff_status, analysis?.tariff_error);
  if (network) stages.push(network);
  // Priced, but without network charges: the invoice parse re-priced retail
  // and metering alone because the bill run never ran (reportStatus
  // .networkNotPriced). Not done, and not a report yet.
  else if (priced && networkNotPriced(bill.shadow_result as ShadowBillResult)) {
    stages.push(fail('network', 'Network charges not priced — the bill run needs usage data for the whole period'));
  }
  else if (priced && networkEnergyNotPriced(bill)) {
    stages.push(fail('network', 'Network energy not priced — usage data for the period was not ready when the bill ran; re-run the bill'));
  }
  else if (priced) stages.push(pass('network'));
  else if (noTariff) stages.push(fail('network', capitalise(blocked!)));
  else stages.push(waiting('network', 'Network charges not calculated yet'));

  const tou = analysisStage('tou', analysis?.tou_status, analysis?.tou_error);
  if (tou) stages.push(tou);
  else if (priced) stages.push(pass('tou'));
  else stages.push(waiting('tou', 'Retail TOU charges not calculated yet'));

  // 8. Bill run calculated.
  if (priced) stages.push(pass('calculated'));
  else if (bill.bill_run_status === 'running') stages.push(running('calculated', 'Bill run in progress'));
  else if (bill.bill_run_status === 'error') {
    stages.push(fail('calculated', `Bill run failed: ${bill.bill_run_error ?? 'unknown error'}`));
  } else stages.push(waiting('calculated', 'Not calculated yet — select it and Run bill run'));

  // 9. Report — exists whenever there is an expected invoice and a retailer
  // invoice to compare it with (reportStatus.hasReport), stamped or not.
  if (hasReport(bill)) stages.push(pass('report'));
  else if (priced && !invoice) stages.push(waiting('report', 'Priced — needs the retailer invoice for a report'));
  else stages.push(waiting('report', 'Created after the bill run'));

  const firstOpen = stages.find((s) => s.state !== 'pass');
  const current = firstOpen?.key ?? 'done';
  return {
    stages,
    current,
    phase: phaseOf(current),
    label: firstOpen?.detail ?? 'Report ready',
  };
}

type AnalysisRowLike = AnalysisLike & {
  bill_id: string | null;
  site_id: string;
  period_from: string;
  period_to: string;
};

/**
 * Index analyses so each bill finds its own: by bill_id where the run recorded
 * it, else by the exact site and period (a stepper run from the site page).
 */
export function analysisFinder<T extends AnalysisRowLike>(analyses: T[]) {
  const byBill = new Map<string, T>();
  const byPeriod = new Map<string, T>();
  for (const a of analyses) {
    if (a.bill_id) byBill.set(a.bill_id, a);
    byPeriod.set(`${a.site_id}|${a.period_from}|${a.period_to}`, a);
  }
  return (bill: Pick<BvBill, 'id' | 'site_id' | 'bill_from' | 'bill_to'>): T | null =>
    byBill.get(bill.id) ?? byPeriod.get(`${bill.site_id}|${bill.bill_from}|${bill.bill_to}`) ?? null;
}

/** Earliest stage first — the bills furthest from a report lead the board. */
export const PHASE_ORDER: PipelinePhase[] = ['period', 'parsed', 'nmi', 'calculated', 'report', 'done'];

function phaseOf(key: StageKey | 'done'): PipelinePhase {
  if (key === 'usage' || key === 'network' || key === 'tou') return 'calculated';
  if (key === 'assigned') return 'period';
  return key;
}

function capitalise(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
